/* A single shared schedule, never a per-visitor countdown. */
if (!customElements.get('volt-product-promo')) {
  class VoltProductPromo extends HTMLElement {
    static timing(campaign, now) {
      const start = campaign.start ? Date.parse(campaign.start) : null;
      const stop = campaign.end ? Date.parse(campaign.end) : null;
      if ((start !== null && !Number.isFinite(start)) || (stop !== null && !Number.isFinite(stop))) return null;
      if ((start !== null && now < start) || (stop !== null && now >= stop)) return null;
      if (campaign.mode === 'evergreen') {
        const days = Number(campaign.days);
        if (start === null || !Number.isInteger(days) || days < 1 || days > 365) return null;
        const interval = days * 86400000;
        const cycleEnd = start + (Math.floor((now - start) / interval) + 1) * interval;
        return { end: stop === null ? cycleEnd : Math.min(cycleEnd, stop) };
      }
      if (campaign.mode !== 'scheduled' || stop === null || (start !== null && stop <= start)) return null;
      return { end: stop };
    }

    static select(campaigns, variant, now = Date.now()) {
      return campaigns.map(campaign => ({ campaign, timing: this.timing(campaign, now) }))
        .filter(({ campaign, timing }) => timing && ['standard', 'compact'].includes(campaign.style) &&
          (!campaign.discountOnly || variant.savingAmount > 0) &&
          // A savings claim must never be shown for a variant without a discount.
          (variant.savingAmount > 0 || !/\{saving(?:_percent)?\}/.test(
            campaign.style === 'compact' ? campaign.compactMessage : campaign.title + campaign.subtitle
          )) && (campaign.countdown || (campaign.style === 'compact' ? campaign.compactMessage : campaign.title || campaign.subtitle)))
        .sort((a, b) => (Number(b.campaign.priority) || 0) - (Number(a.campaign.priority) || 0) ||
          (a.campaign.handle < b.campaign.handle ? -1 : a.campaign.handle > b.campaign.handle ? 1 : 0))[0] || null;
    }

    static timeText(milliseconds) {
      const total = Math.max(0, Math.ceil(milliseconds / 1000));
      const days = Math.floor(total / 86400);
      const hours = Math.floor(total % 86400 / 3600);
      const minutes = Math.floor(total % 3600 / 60);
      const seconds = total % 60;
      const pad = value => String(value).padStart(2, '0');
      return `${days ? `${pad(days)}d ` : ''}${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
    }

    connectedCallback() {
      if (this.controller) return;
      try {
        this.campaigns = JSON.parse(this.querySelector('[data-promo-campaigns]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-promo-variants]').textContent);
      } catch (_) { return; }
      this.variantId = this.dataset.variantId;
      this.scope = this.closest('product-info') || this.closest('.shopify-section') || this.parentElement;
      this.titleNode = this.querySelector('[data-promo-title]');
      this.subtitle = this.querySelector('[data-promo-subtitle]');
      this.message = this.querySelector('[data-promo-compact-message]');
      this.countdown = this.querySelector('[data-promo-countdown]');
      this.time = this.querySelector('[data-promo-time]');
      this.dot = this.querySelector('.campaign-promo__dot');
      this.controller = new AbortController();
      const { signal } = this.controller;
      document.addEventListener('variant:changed', event => {
        const variant = event.detail?.variant;
        if (!variant || !this.variants[String(variant.id)]) return;
        this.variantId = String(variant.id);
        this.update();
      }, { signal });
      // Also handles theme-editor or third-party variant pickers updating the form.
      this.scope.addEventListener('change', event => {
        if (event.target?.name === 'id' && this.variants[event.target.value]) {
          this.variantId = event.target.value;
          this.update();
        }
      }, { signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.update(); }, { signal });
      this.update();
      this.interval = setInterval(() => { if (!document.hidden) this.update(); }, 1000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearInterval(this.interval);
    }

    fillText(element, template, variant) {
      // Text nodes only: merchant copy and variable values are never interpreted as HTML.
      const parts = template.split(/(\{(?:saving|saving_percent|price)\})/g);
      const nodes = parts.map(part => {
        const key = part.match(/^\{(saving|saving_percent|price)\}$/)?.[1];
        if (!key) return document.createTextNode(part);
        const node = document.createElement('strong');
        node.textContent = variant[key];
        return node;
      });
      element.replaceChildren(...nodes);
      element.hidden = !template;
    }

    update(now = Date.now()) {
      const variant = this.variants[this.variantId];
      const selected = variant && VoltProductPromo.select(this.campaigns, variant, now);
      if (!selected) { this.hidden = true; this.lastKey = null; return; }
      const { campaign, timing } = selected;
      const key = `${campaign.handle}:${this.variantId}`;
      if (key !== this.lastKey) {
        this.dataset.style = campaign.style;
        this.dataset.campaign = campaign.handle;
        const compact = campaign.style === 'compact';
        this.querySelector('.campaign-promo__content').hidden = compact;
        this.fillText(this.titleNode, campaign.title, variant);
        this.fillText(this.subtitle, campaign.subtitle, variant);
        this.fillText(this.message, compact ? campaign.compactMessage : '', variant);
        this.countdown.hidden = !campaign.countdown;
        this.dot.hidden = !(compact && campaign.countdown && campaign.compactMessage);
        this.lastKey = key;
      }
      this.time.textContent = VoltProductPromo.timeText(timing.end - now);
      this.hidden = false;
    }
  }
  customElements.define('volt-product-promo', VoltProductPromo);
}
